import { useState, useEffect } from "react";
import api from "@/utils/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { TrendingUp, TrendingDown, PoundSterling } from "lucide-react";
import ExportButtons from "@/components/ExportButtons";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin as isAdminRole } from "@/utils/roles";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import ThreeWindowRow from "@/components/ThreeWindowRow";

const COLORS = ["#0284C7", "#16A34A", "#D97706", "#DC2626", "#7C3AED", "#06B6D4", "#F59E0B", "#EF4444"];

export default function AnalyticsPage() {
  useDocumentTitle("Analytics", "Financial and operational reporting.");
  const { user } = useAuth();
  const canSeeFinancials = isAdminRole(user?.role);
  const [certData, setCertData] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [fy, setFy] = useState("");  // "" = current FY on the server
  // Drill-in state for click-through on Expired / Expiring tiles + category chart
  const [drillStatus, setDrillStatus] = useState(null);
  const [drillCategory, setDrillCategory] = useState("");
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    const dashUrl = fy ? `/analytics/dashboard?fy=${fy}` : "/analytics/dashboard";
    const finUrl = fy ? `/analytics/financial?fy=${fy}` : "/analytics/financial";
    const calls = [api.get("/analytics/certificates"), api.get(dashUrl)];
    if (canSeeFinancials) calls.push(api.get(finUrl));
    Promise.all(calls).then((results) => {
      setCertData(results[0].data);
      setDashData(results[1].data);
      if (canSeeFinancials && results[2]) setFinancialData(results[2].data);
    }).catch((err) => {
      console.error("Failed to load analytics:", err);
    }).finally(() => setLoading(false));
  }, [canSeeFinancials, fy]);

  const openDrill = async (status, category = "") => {
    setDrillStatus(status);
    setDrillCategory(category);
    setDrillLoading(true);
    setDrillRows([]);
    try {
      const q = new URLSearchParams({ status });
      if (category) q.set("category", category);
      const { data } = await api.get(`/analytics/cert-details?${q.toString()}`);
      setDrillRows(data.certificates || []);
    } catch (err) {
      console.error("Failed to load drill-in:", err);
    } finally {
      setDrillLoading(false);
    }
  };

  // Recharts fires this on any stacked segment click; the payload's `name`
  // is the Y-axis category (e.g. "Domestic Gas") and `tooltipPayload` tells
  // us which colour band was clicked. We map the band → cert status.
  const onCategorySegmentClick = (bandStatus) => (payload) => {
    if (!payload || !payload.name) return;
    if (bandStatus === "active") return;  // "active" isn't a chase list
    openDrill(bandStatus, payload.name);
  };

  // Build a list of the last 5 financial years the user can pick from —
  // the current one lives on `dashData.fy_start_year`.
  const currentFy = dashData?.fy_start_year || new Date().getFullYear() - (new Date().getMonth() < 7 ? 1 : 0);
  const fyOptions = [0, 1, 2, 3, 4].map(i => currentFy - i);
  const fyLabel = (y) => `${y}/${String(y + 1).slice(-2)}`;

  if (loading) return <div className="animate-pulse text-muted-foreground" data-testid="analytics-loading">Loading analytics...</div>;

  const statusData = [
    { name: "Active", value: dashData?.certificate_by_status?.active || 0 },
    { name: "Expiring", value: dashData?.certificate_by_status?.expiring_soon || 0 },
    { name: "Expired", value: dashData?.certificate_by_status?.expired || 0 },
  ].filter(d => d.value > 0);

  const categoryData = Object.entries(dashData?.certificate_by_category || {}).map(([cat, counts]) => ({
    name: cat.length > 15 ? cat.substring(0, 15) + "..." : cat,
    active: counts.active || 0, expiring: counts.expiring_soon || 0, expired: counts.expired || 0,
  }));

  const fmt = (v) => `£${(v || 0).toLocaleString()}`;
  const totals = financialData?.totals || {};

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList className="h-8" data-testid="analytics-tabs">
            <TabsTrigger value="overview" className="text-xs px-3 py-1">Certificates</TabsTrigger>
            {canSeeFinancials && <TabsTrigger value="financial" className="text-xs px-3 py-1">Financial</TabsTrigger>}
          </TabsList>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Financial Year (Aug–Jul)
              <select
                value={fy || currentFy}
                onChange={(e) => setFy(String(e.target.value))}
                className="h-8 text-xs border border-border rounded-md bg-white px-2"
                data-testid="fy-selector"
              >
                {fyOptions.map(y => (
                  <option key={y} value={y}>{fyLabel(y)}{y === currentFy ? " (current)" : ""}</option>
                ))}
              </select>
            </label>
            <ExportButtons endpoint={tab === "financial" ? "financial" : "certificates"} label={tab === "financial" ? "Financial" : "Certificates"} />
          </div>
        </div>

        {/* Certificates Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Row 1: All-time totals + drill-in tiles for chase list */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-border rounded-md p-4 bg-white" data-testid="kpi-total-members"><p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Members</p><p className="font-heading font-bold text-2xl">{dashData?.total_members || 0}</p><p className="text-[11px] text-muted-foreground mt-0.5">All time</p></div>
            <div className="border border-border rounded-md p-4 bg-white" data-testid="kpi-total-certificates"><p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Certificates</p><p className="font-heading font-bold text-2xl">{dashData?.total_certificates || 0}</p><p className="text-[11px] text-muted-foreground mt-0.5">All time</p></div>
            <button type="button" onClick={() => openDrill("expiring_soon")} className="cursor-pointer text-left border border-amber-200 rounded-md p-4 bg-amber-50 hover:bg-amber-100 hover:shadow-sm transition-all" data-testid="analytics-expiring-tile">
              <p className="text-xs text-amber-800 uppercase tracking-wide mb-1 flex items-center justify-between">Expiring Soon <span className="text-amber-500">Click to view →</span></p>
              <p className="font-heading font-bold text-2xl text-amber-700">{dashData?.expiring_soon || 0}</p>
            </button>
            <button type="button" onClick={() => openDrill("expired")} className="cursor-pointer text-left border border-red-200 rounded-md p-4 bg-red-50 hover:bg-red-100 hover:shadow-sm transition-all" data-testid="analytics-expired-tile">
              <p className="text-xs text-red-800 uppercase tracking-wide mb-1 flex items-center justify-between">Expired <span className="text-red-500">Click to view →</span></p>
              <p className="font-heading font-bold text-2xl text-red-700">{dashData?.expired || 0}</p>
            </button>
          </div>

          {/* Rows 2–4: Three-window (This FY · Past 5 FYs · All-time) tiles
              for each activity metric so Jamie can compare in one glance. */}
          <ThreeWindowRow
            title="Students Trained"
            fyLabel={dashData?.fy_label}
            past5Label={dashData?.past5_label}
            fyValue={dashData?.fy_students_trained}
            past5Value={dashData?.past5_students_trained}
            allValue={dashData?.all_time_students_trained}
            fyHint="Unique learners on courses this FY"
            past5Hint="Unique learners across the last 5 FYs"
            allHint="Distinct learners on any course ever"
            testid="students-trained"
          />
          <ThreeWindowRow
            title="Certificates Issued"
            fyLabel={dashData?.fy_label}
            past5Label={dashData?.past5_label}
            fyValue={dashData?.fy_certs_issued}
            past5Value={dashData?.past5_certs_issued}
            allValue={dashData?.total_certificates}
            fyHint={`Issued ${dashData?.fy_from || ""} → ${dashData?.fy_to || ""}`}
            past5Hint="Issued across the last 5 FYs"
            allHint="Every certificate ever issued"
            testid="certs-issued"
          />
          <ThreeWindowRow
            title="Courses Delivered"
            fyLabel={dashData?.fy_label}
            past5Label={dashData?.past5_label}
            fyValue={dashData?.fy_courses_delivered}
            past5Value={dashData?.past5_courses_delivered}
            allValue={dashData?.total_courses}
            fyHint="Courses started this FY"
            past5Hint="Courses started in the last 5 FYs"
            allHint="Every course ever scheduled"
            testid="courses-delivered"
          />

          {/* Catalogue-level counts */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-border rounded-md p-4 bg-white" data-testid="kpi-total-qualifications"><p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Qualifications</p><p className="font-heading font-bold text-2xl">{dashData?.total_qualifications || 0}</p><p className="text-[11px] text-muted-foreground mt-0.5">Catalogue size</p></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-border rounded-md p-4 bg-white" data-testid="cert-status-pie">
              <h3 className="font-heading font-semibold text-sm mb-4">Certificate Status</h3>
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart><Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {statusData.map((entry, idx) => <Cell key={entry.name} fill={COLORS[idx]} />)}
                  </Pie><Tooltip /><Legend /></PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-muted-foreground text-center py-8">No certificate data</p>}
            </div>
            <div className="border border-border rounded-md p-4 bg-white" data-testid="expiry-timeline">
              <h3 className="font-heading font-semibold text-sm mb-4">Expiry Timeline (12 Months)</h3>
              {(certData?.expiry_timeline || []).some(m => m.count > 0) ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={certData?.expiry_timeline || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#0284C7" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-muted-foreground text-center py-8">No expiry data</p>}
            </div>
          </div>
          {categoryData.length > 0 && (
            <div className="border border-border rounded-md p-4 bg-white" data-testid="certs-by-category">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-sm">Certificates by Category</h3>
                <p className="text-[11px] text-muted-foreground">Click an <span className="text-amber-600 font-medium">amber</span> or <span className="text-red-600 font-medium">red</span> bar to open the chase list</p>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} /><Tooltip /><Legend />
                  <Bar dataKey="active" fill="#16A34A" stackId="stack" name="Active" />
                  <Bar dataKey="expiring" fill="#D97706" stackId="stack" name="Expiring" cursor="pointer" onClick={onCategorySegmentClick("expiring_soon")} />
                  <Bar dataKey="expired" fill="#DC2626" stackId="stack" name="Expired" cursor="pointer" onClick={onCategorySegmentClick("expired")} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4 mt-4">
          {/* Financial KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-border rounded-md p-4 bg-white">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Revenue — FY {dashData?.fy_label || ""}</p>
              <p className="font-heading font-bold text-2xl text-emerald-600">{fmt(totals.total_revenue)}</p>
            </div>
            <div className="border border-border rounded-md p-4 bg-white">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Costs</p>
              <p className="font-heading font-bold text-2xl text-red-600">{fmt(totals.total_cost)}</p>
            </div>
            <div className="border border-border rounded-md p-4 bg-white">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Net Profit</p>
              <div className="flex items-center gap-2">
                <p className={`font-heading font-bold text-2xl ${totals.total_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(totals.total_profit)}</p>
                {totals.total_profit >= 0 ? <TrendingUp size={18} className="text-emerald-500" /> : <TrendingDown size={18} className="text-red-500" />}
              </div>
            </div>
            <div className="border border-border rounded-md p-4 bg-white">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Students</p>
              <p className="font-heading font-bold text-2xl">{totals.total_students || 0}</p>
            </div>
          </div>

          {/* Monthly P&L Chart */}
          {(financialData?.months || []).length > 0 && (
            <div className="border border-border rounded-md p-4 bg-white" data-testid="monthly-pnl-chart">
              <h3 className="font-heading font-semibold text-sm mb-4">Monthly Revenue vs Cost</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={financialData.months}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                  <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#16A34A" name="Revenue" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="cost" fill="#DC2626" name="Cost" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Profit Line */}
          {(financialData?.months || []).length > 0 && (
            <div className="border border-border rounded-md p-4 bg-white" data-testid="profit-line-chart">
              <h3 className="font-heading font-semibold text-sm mb-4">Profit Trend</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={financialData.months}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                  <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                  <Line type="monotone" dataKey="profit" stroke="#0284C7" strokeWidth={2} name="Profit" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly Breakdown Table */}
          {(financialData?.months || []).length > 0 && (
            <div className="border border-border rounded-md bg-white" data-testid="monthly-breakdown">
              <div className="p-4 border-b border-border"><h3 className="font-heading font-semibold text-sm">Monthly Breakdown</h3></div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">
                  <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Month</th>
                  <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Courses</th>
                  <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Students</th>
                  <th className="px-4 py-2 font-medium text-muted-foreground text-xs text-right">Revenue</th>
                  <th className="px-4 py-2 font-medium text-muted-foreground text-xs text-right">Cost</th>
                  <th className="px-4 py-2 font-medium text-muted-foreground text-xs text-right">Profit</th>
                </tr></thead>
                <tbody>
                  {financialData.months.map(m => (
                    <tr key={m.month} className="border-b border-border">
                      <td className="px-4 py-2 font-medium">{m.month}</td>
                      <td className="px-4 py-2">{m.courses}</td>
                      <td className="px-4 py-2">{m.students}</td>
                      <td className="px-4 py-2 text-right text-emerald-600 font-medium">{fmt(m.revenue)}</td>
                      <td className="px-4 py-2 text-right text-red-600">{fmt(m.cost)}</td>
                      <td className={`px-4 py-2 text-right font-bold ${m.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(m.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Drill-in modal — click Expiring / Expired on the Analytics tiles or a category bar segment */}
      <Dialog open={drillStatus !== null} onOpenChange={(v) => { if (!v) { setDrillStatus(null); setDrillCategory(""); setDrillRows([]); } }}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col" data-testid="cert-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {drillStatus === "expired" ? "Expired certificates — chase list" : "Certificates expiring soon"}
              {drillCategory && <span className="ml-2 text-sm font-normal text-slate-500">· {drillCategory}</span>}
              {drillRows.length > 0 && <span className="ml-2 text-sm font-normal text-muted-foreground">({drillRows.length})</span>}
            </DialogTitle>
          </DialogHeader>
          {drillLoading ? (
            <div className="animate-pulse text-muted-foreground text-sm py-8 text-center">Loading…</div>
          ) : drillRows.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              Nothing here — every {drillStatus === "expired" ? "expired" : "expiring"} cert{drillCategory ? ` in ${drillCategory}` : ""} has been handled.
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 -mx-6 px-6">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white border-b border-border text-xs text-muted-foreground text-left">
                  <tr>
                    <th className="px-2 py-2 font-medium">Learner</th>
                    <th className="px-2 py-2 font-medium">Contact</th>
                    <th className="px-2 py-2 font-medium">Address</th>
                    <th className="px-2 py-2 font-medium">Qualification</th>
                    <th className="px-2 py-2 font-medium">Expiry</th>
                    <th className="px-2 py-2 font-medium">Last chased</th>
                  </tr>
                </thead>
                <tbody>
                  {drillRows.map(r => (
                    <tr key={r.id} className="border-b border-border hover:bg-slate-50" data-testid={`drill-row-${r.id}`}>
                      <td className="px-2 py-2 font-medium align-top">
                        {r.member_id ? (
                          <a href={`/members?open=${r.member_id}`} className="text-sky-700 hover:underline">{r.member_name || "—"}</a>
                        ) : (r.member_name || "—")}
                        {!drillCategory && r.qualification_category && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">{r.qualification_category}</div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs text-muted-foreground align-top">
                        <div>{r.member_email || "—"}</div>
                        <div>{r.member_phone || ""}</div>
                      </td>
                      <td className="px-2 py-2 text-xs text-muted-foreground align-top" data-testid={`drill-addr-${r.id}`}>
                        {r.member_address || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 align-top">
                        {r.qualification_name}
                        {r.certificate_number && <div className="text-xs text-muted-foreground">Cert #{r.certificate_number}</div>}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Badge variant="outline" className={r.computed_status === "expired" ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200"}>
                          {r.expiry_date || "—"}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-xs align-top">
                        {r.last_emailed_at ? (
                          <>
                            <div>{new Date(r.last_emailed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                            <div className="text-muted-foreground">by {r.last_emailed_by || "system"}</div>
                          </>
                        ) : (
                          <span className="text-red-600">Never — chase now</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
